import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useAuthStore } from '@/stores/authStore'

interface TenantState {
  activeTenantId: string | null
  setActiveTenant: (tenantId: string | null) => void
}

export const useTenantStore = create<TenantState>()(
  persist(
    (set) => ({
      activeTenantId: null,
      setActiveTenant: (tenantId) => set({ activeTenantId: tenantId }),
    }),
    {
      name: 'cockpit.tenant',
    },
  ),
)

/**
 * Resolved tenant scope for list queries. A tenant_admin is always pinned to
 * its own tenant; a super_admin follows the active selection ('all' = every
 * tenant). `scope` is the query-key segment; `tenantId` is the filter value
 * (null when unscoped).
 */
export function useActiveTenantScope(): { scope: string; tenantId: string | null } {
  const admin = useAuthStore((s) => s.admin)
  const activeTenantId = useTenantStore((s) => s.activeTenantId)
  if (admin?.role === 'tenant_admin') {
    const tenantId = admin.tenantId
    return { scope: tenantId ?? 'all', tenantId }
  }
  return { scope: activeTenantId ?? 'all', tenantId: activeTenantId }
}

/** Merge a tenantId filter into params, omitting it when scope is 'all' (null). */
export function withTenant<T extends object>(
  params: T,
  tenantId: string | null,
): T & { tenantId?: string } {
  if (tenantId === null) return params
  return { ...params, tenantId }
}
